// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Divider } from '@mui/material';
import { useAppSelector } from 'Hooks';
import { dollars } from 'Utils/numberFormat';
import { licenseFeePercentText } from 'Utils/licenseFee';
import { RootState } from 'Store';
import EditRate from './editRate';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

// the ladder's Entry tier is "Standard" in the database
const tierDisplayName = (name: string | null) => (name === 'Standard' ? 'Entry' : name);

const SOURCE_LABELS: Record<AccountRateSource, string> = {
  contract_rate: 'contract rate',
  plan_band: 'plan tier rate',
  ladder_tier: 'Credit Ladder',
  negotiated: 'negotiated rate',
  payg: 'pay-as-you-go rate',
  payg_override: 'pay-as-you-go rate, negotiated',
  none: '',
};

/**
 * What the account pays per credit (one rate per account, 2026-10-29): one figure, read by its AEC orders, its catalog
 * orders and every credit beyond its balance alike, with where it comes from, who set it and the license fee the
 * account's setting puts on top. One Edit Rate changes it, whatever kind of account it is; the change log sits under
 * the controls.
 */
export const PricePerCredit = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const { org } = useAppSelector((state: RootState) => state.admin);
  const account = org?.accountRate || null;
  const source: AccountRateSource = account?.source || 'none';
  const rate = typeof account?.dollarPerCredit === 'number' ? account.dollarPerCredit : null;
  const history = account?.history || [];
  const latest = history.find((entry) => typeof entry.rate === 'number') || null;
  const licenseFeeMode = org?.licenseFee?.mode || 'none';
  const feeApplies = licenseFeeMode === 'added';
  const planName = account?.planName || null;
  const isPayg = planName === 'no_commit';
  const hasBalance = source !== 'none' && !isPayg;
  // the rate's default: the plan band for a plan, the ladder for pay-as-you-go; a contract rate has none
  let revertLabel: string | null = null;
  if (source === 'ladder_tier' || source === 'negotiated') {
    revertLabel = 'Revert to Plan Rate';
  } else if (source === 'payg_override') {
    revertLabel = 'Revert to Pay-as-you-go Rate';
  }

  const renderRateLine = () => {
    if (source === 'none') {
      return <Typography sx={styles.muted}>No rate — assign a plan.</Typography>;
    }
    if (rate === null) {
      return <Typography sx={styles.muted}>No contract rate — set one.</Typography>;
    }
    const label = source === 'ladder_tier' ? `${tierDisplayName(account?.tierName || null) || 'ladder tier'} (Credit Ladder)` : SOURCE_LABELS[source];
    return <Typography sx={styles.figureLine}>{`${dollars(rate)} per credit · ${label}`}</Typography>;
  };

  const renderSource = () => {
    if (source === 'none' || rate === null) return null;
    if (latest && typeof latest.rate === 'number' && latest.changedBy) {
      return `Set by ${latest.changedBy} on ${moment(latest.createdAt).format('LL')}: ${latest.notes}`;
    }
    if (source === 'negotiated' || source === 'ladder_tier') {
      return 'Set outside Treasure Chest.';
    }
    if (source === 'plan_band') {
      return "The plan band's rate, from the credits purchased.";
    }
    if (source === 'payg') {
      return 'The pay-as-you-go ladder rate.';
    }
    if (source === 'payg_override') {
      return 'A negotiated pay-as-you-go rate.';
    }
    return 'The contract rate.';
  };

  return (
    <Box>
      <Typography variant="h4" sx={styles.sectionTitle}>Price per Credit</Typography>
      <Divider sx={styles.divider} />
      <Box sx={styles.block}>
        {renderRateLine()}
        {renderSource() && <Typography variant="body2" sx={styles.sourceText}>{renderSource()}</Typography>}
        {feeApplies && source !== 'none' && (
          <Typography variant="body2" sx={styles.sourceText}>{`+ ${licenseFeePercentText(org?.licenseFee?.percent)} license fee on each order.`}</Typography>
        )}
        {hasBalance && <Typography variant="body2" sx={styles.sourceText}>The same rate applies beyond the balance.</Typography>}
        <Typography variant="body2" sx={styles.caption}>
          One rate for every order: AEC and catalog orders price at it, and so does every credit beyond the balance.
        </Typography>
        {source !== 'none' && <EditRate revertLabel={revertLabel} />}
      </Box>
    </Box>
  );
};

export default PricePerCredit;
